import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Car, ChevronLeft, CreditCard, Loader2, Shield, Check, Gauge, Users, Settings2, Fuel,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api, { carBroker } from '@/lib/api';
import { buildPathForLocale } from '@/lib/intl-seo';
import PaymentBrandLogos from '@/components/payment/PaymentBrandLogos';
import {
  buildZiraatCardPayloadFromForm,
  expiryMonthOptions,
  expiryYearOptions,
  formatCardNumberInput,
  validatePaymentCard,
} from '@/lib/paymentCardForm';
import { useCurrency } from '@/lib/currency-context';
import {
  vehicleImageUrl,
  vehicleToken,
  vehicleVendorName,
  vehicleSeatsLabel,
  vehicleTransmissionLabel,
  vehicleFuelLabel,
  vehicleCategoryLabel,
  vehicleTypeLabel,
  vehicleConditions,
  toBookingVehicleSnapshot,
} from '@/lib/carBrokerVehicle';

function pickToken(vehicle) {
  return vehicleToken(vehicle);
}

export default function CarRentalBookingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  const lc = (i18n.language || 'tr').slice(0, 2);
  const carsHome = buildPathForLocale(lc, ['car-rentals']);

  const [ctx, setCtx] = useState(null);
  const [extras, setExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [customer, setCustomer] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    identity_number: '',
    address: '',
    note: '',
    flight_arrival: '',
  });

  const [cardInfo, setCardInfo] = useState({
    card_number: '',
    card_holder: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
  });

  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      const msg = decodeURIComponent(err.replace(/\+/g, ' '));
      setPaymentError(msg);
      toast.error(msg, { duration: 10000 });
    }
  }, [searchParams]);

  useEffect(() => {
    const raw = sessionStorage.getItem('pendingCarBrokerBooking');
    if (!raw) {
      toast.error(t('carRental.sessionExpired', 'Oturum süresi doldu, tekrar arayın'));
      navigate(carsHome);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!pickToken(parsed.vehicle)) {
        throw new Error('token');
      }
      setCtx(parsed);
    } catch {
      navigate(carsHome);
    }
  }, [navigate, t, lc, carsHome]);

  useEffect(() => {
    if (!ctx?.vehicle) return;
    const token = pickToken(ctx.vehicle);
    const lang = ctx.search?.languageCode || 'TR';
    setLoadingExtras(true);
    carBroker
      .extras(token, lang)
      .then((res) => {
        const data = res.data?.data || res.data;
        const list = data?.extras || (Array.isArray(data) ? data : []);
        setExtras(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(err);
        setExtras([]);
      })
      .finally(() => setLoadingExtras(false));
  }, [ctx]);

  const currency =
    ctx?.vehicle?.currencyCode || ctx?.search?.currencyCode || 'EUR';
  const vehicleTotal = Number(
    ctx?.vehicle?.totalPrice ?? ctx?.vehicle?.total ?? 0
  );

  const extrasTotal = useMemo(() => {
    return extras.reduce((sum, ex) => {
      const id = String(ex.extraId ?? ex.extraCode);
      const qty = selectedExtras[id] || 0;
      return sum + qty * Number(ex.price || 0);
    }, 0);
  }, [extras, selectedExtras]);

  const grandTotal = vehicleTotal + extrasTotal;

  const toggleExtra = (ex, delta) => {
    const id = String(ex.extraId ?? ex.extraCode);
    setSelectedExtras((prev) => {
      const cur = prev[id] || 0;
      const next = Math.max(0, cur + delta);
      const max = ex.extraQuantityIncreasable === false ? 1 : 9;
      const capped = Math.min(next, max);
      const copy = { ...prev };
      if (capped <= 0) delete copy[id];
      else copy[id] = capped;
      return copy;
    });
  };

  const handleSubmit = async () => {
    if (!ctx) return;
    if (!customer.name?.trim() || !customer.surname?.trim()) {
      toast.error(t('carRental.fillContactInfo'));
      return;
    }
    if (!customer.email?.includes('@') || !customer.phone?.trim()) {
      toast.error(t('carRental.fillContactInfo'));
      return;
    }
    if (ctx.vehicle?.personalNumberRequired && !customer.identity_number?.trim()) {
      toast.error(t('carRental.identityRequired', 'TC / Pasaport zorunlu'));
      return;
    }
    const cardErr = validatePaymentCard(cardInfo);
    if (cardErr) {
      toast.error(cardErr);
      return;
    }
    if (!acceptTerms) {
      toast.error(t('carRental.acceptTerms', 'Şartları kabul edin'));
      return;
    }

    setSubmitting(true);
    setPaymentError(null);
    try {
      const vehicle = ctx.vehicle;
      const search = ctx.search;
      const selectedList = extras
        .map((ex) => {
          const id = String(ex.extraId ?? ex.extraCode);
          const qty = selectedExtras[id] || 0;
          if (!qty) return null;
          return {
            extraId: ex.extraId,
            extraCode: ex.extraCode,
            code: ex.Code || ex.code || ex.extraCode,
            extraName: ex.extraName,
            price: Number(ex.price || 0),
            supplier_price:
              ex.supplier_price != null ? Number(ex.supplier_price) : undefined,
            quantity: qty,
            currencyCode: ex.currencyCode || currency,
          };
        })
        .filter(Boolean);

      const bookingRes = await carBroker.createBooking({
        vehicle: {
          ...toBookingVehicleSnapshot(vehicle, currency),
          totalPrice: vehicleTotal,
        },
        customer,
        pickupLocationId: search.pickupLocationId,
        returnLocationId: search.returnLocationId,
        pickupLocationName: search.pickupLocationName,
        returnLocationName: search.returnLocationName,
        pickupDate: search.pickupDate,
        returnDate: search.returnDate,
        pickupTime: search.pickupTime,
        returnTime: search.returnTime,
        languageCode: search.languageCode || 'TR',
        currencyCode: currency,
        driverAge: search.driverAge || 30,
        extras: selectedList,
        payment_method: 'ziraat',
        paid_amount: grandTotal,
      });

      if (!bookingRes.data?.success) {
        throw new Error(bookingRes.data?.message || 'Booking failed');
      }

      const bookingId = bookingRes.data.booking_id;
      sessionStorage.setItem('pendingCarBrokerBookingId', bookingId);

      // Ziraat charges TRY; backend converts EUR/USD → TRY
      const chargeAmount = Number(
        bookingRes.data.booking_details?.total_try ??
          bookingRes.data.booking_details?.total ??
          grandTotal
      );
      if (chargeAmount <= 0) throw new Error('Invalid amount');

      const cardPayload = buildZiraatCardPayloadFromForm(cardInfo);
      const paymentResponse = await api.post('/ziraat/initiate', {
        order_id: bookingId,
        ...cardPayload,
        amount: chargeAmount,
        currency: 'TRY',
        installment: 1,
        customer_email: customer.email,
      });

      if (paymentResponse.data.success && paymentResponse.data.html_form) {
        document.open();
        document.write(paymentResponse.data.html_form);
        document.close();
        return;
      }

      // Fallback: confirm without 3D (dev)
      await carBroker.confirmPayment(bookingId);
      navigate(`${buildPathForLocale(lc, ['car-rentals', 'booking', 'success'])}?id=${bookingId}`);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t('common.error');
      setPaymentError(typeof msg === 'string' ? msg : t('common.error'));
      toast.error(typeof msg === 'string' ? msg : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const vehicle = ctx.vehicle;
  const search = ctx.search;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate(carsHome)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('common.back', 'Geri')}
        </Button>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  {vehicle.vehicleName}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div className="rounded-lg bg-white border p-3 flex justify-center">
                  <img
                    src={vehicleImageUrl(vehicle)}
                    alt={vehicle.vehicleName || 'car'}
                    className="max-h-40 object-contain"
                    onError={(e) => {
                      e.currentTarget.src =
                        'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=600';
                    }}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {vehicleCategoryLabel(vehicle) && (
                    <Badge className="bg-sky-700">{vehicleCategoryLabel(vehicle)}</Badge>
                  )}
                  {vehicleTypeLabel(vehicle) && (
                    <Badge variant="secondary">{vehicleTypeLabel(vehicle)}</Badge>
                  )}
                  {vehicleVendorName(vehicle) && (
                    <Badge variant="outline">{vehicleVendorName(vehicle)}</Badge>
                  )}
                </div>
                <div className="text-muted-foreground space-y-1">
                  <p>
                    {vehicle.pickupLocationName || search.pickupLocationName} →{' '}
                    {vehicle.returnLocationName || search.returnLocationName}
                  </p>
                  <p>
                    {search.pickupDate} {search.pickupTime} — {search.returnDate}{' '}
                    {search.returnTime}
                    {vehicle.rentalDuration != null && (
                      <> · {vehicle.rentalDuration} {t('carRental.day')}</>
                    )}
                  </p>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                  {vehicleTransmissionLabel(vehicle) && (
                    <span className="inline-flex items-center gap-1">
                      <Settings2 className="w-3.5 h-3.5" />
                      {vehicleTransmissionLabel(vehicle)}
                    </span>
                  )}
                  {vehicleFuelLabel(vehicle) && (
                    <span className="inline-flex items-center gap-1">
                      <Fuel className="w-3.5 h-3.5" />
                      {vehicleFuelLabel(vehicle)}
                    </span>
                  )}
                  {vehicleSeatsLabel(vehicle) && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {vehicleSeatsLabel(vehicle)}
                    </span>
                  )}
                  {vehicle.totalKMLimit != null && Number(vehicle.totalKMLimit) > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Gauge className="w-3.5 h-3.5" />
                      {vehicle.totalKMLimit} km
                    </span>
                  )}
                </div>
                {Number(vehicle.depositPrice) > 0 && (
                  <p className="text-amber-800 bg-amber-50 rounded px-2 py-1.5 text-xs">
                    {t('carRental.deposit', 'Depozito')}:{' '}
                    {formatPrice(Number(vehicle.depositPrice), currency)}
                    {vehicle.depositCreditCardRequired
                      ? ` · ${t('carRental.depositCardRequired', 'Kredi kartı gerekli')}`
                      : ''}
                  </p>
                )}
                {vehicle.vendorMinimumDriverAge != null && (
                  <p className="text-xs text-muted-foreground">
                    {t('carRental.minDriverAge', 'Min. yaş')}:{' '}
                    {vehicle.vendorMinimumDriverAge}
                    {vehicle.vendorMinimumDrivingLicenseAge != null && (
                      <>
                        {' · '}
                        {t('carRental.minLicenseYears', 'Ehliyet min. yıl')}:{' '}
                        {vehicle.vendorMinimumDrivingLicenseAge}
                      </>
                    )}
                  </p>
                )}
                {vehicleConditions(vehicle).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {vehicleConditions(vehicle).map((c) => (
                      <Badge
                        key={`${c.conditionId}-${c.conditionCode}`}
                        variant="outline"
                        className="text-[10px] font-normal"
                      >
                        {c.conditionName}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('carRental.extras', 'Ekstralar')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {loadingExtras ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading', 'Yükleniyor...')}
                  </div>
                ) : extras.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('carRental.noExtras', 'Ekstra ürün yok')}
                  </p>
                ) : (
                  extras.map((ex) => {
                    const id = String(ex.extraId ?? ex.extraCode);
                    const qty = selectedExtras[id] || 0;
                    return (
                      <div
                        key={id}
                        className="flex items-center justify-between gap-3 border rounded-lg p-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{ex.extraName}</p>
                          {ex.extraDescription && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {ex.extraDescription}
                            </p>
                          )}
                          <p className="text-sm text-sky-800 mt-1">
                            {formatPrice(Number(ex.price || 0), ex.currencyCode || currency)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => toggleExtra(ex, -1)}
                            disabled={qty <= 0}
                          >
                            −
                          </Button>
                          <span className="w-6 text-center text-sm">{qty}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => toggleExtra(ex, 1)}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('carRental.contactInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t('carRental.customerName')}</Label>
                  <Input
                    value={customer.name}
                    onChange={(e) =>
                      setCustomer((c) => ({ ...c, name: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('carRental.surname', 'Soyad')}</Label>
                  <Input
                    value={customer.surname}
                    onChange={(e) =>
                      setCustomer((c) => ({ ...c, surname: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('carRental.customerEmail')}</Label>
                  <Input
                    type="email"
                    value={customer.email}
                    onChange={(e) =>
                      setCustomer((c) => ({ ...c, email: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('carRental.customerPhone')}</Label>
                  <Input
                    value={customer.phone}
                    onChange={(e) =>
                      setCustomer((c) => ({ ...c, phone: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>
                    {t('carRental.identityNumber', 'TC / Pasaport')}
                    {vehicle.personalNumberRequired ? ' *' : ''}
                  </Label>
                  <Input
                    value={customer.identity_number}
                    onChange={(e) =>
                      setCustomer((c) => ({
                        ...c,
                        identity_number: e.target.value,
                      }))
                    }
                    required={Boolean(vehicle.personalNumberRequired)}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t('carRental.flightArrival', 'Varış uçuş no')}</Label>
                  <Input
                    value={customer.flight_arrival}
                    onChange={(e) =>
                      setCustomer((c) => ({
                        ...c,
                        flight_arrival: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t('carRental.specialRequests')}</Label>
                  <Input
                    value={customer.note}
                    onChange={(e) =>
                      setCustomer((c) => ({ ...c, note: e.target.value }))
                    }
                    placeholder={t('carRental.specialRequestsPlaceholder')}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('carRental.payment', 'Ödeme')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <PaymentBrandLogos />
                {paymentError && (
                  <Alert variant="destructive">
                    <AlertDescription>{paymentError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-1.5">
                  <Label>{t('payment.cardHolder', 'Kart üzerindeki isim')}</Label>
                  <Input
                    value={cardInfo.card_holder}
                    onChange={(e) =>
                      setCardInfo((c) => ({
                        ...c,
                        card_holder: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('payment.cardNumber', 'Kart numarası')}</Label>
                  <Input
                    inputMode="numeric"
                    value={cardInfo.card_number}
                    onChange={(e) =>
                      setCardInfo((c) => ({
                        ...c,
                        card_number: formatCardNumberInput(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('payment.month', 'Ay')}</Label>
                    <Select
                      value={cardInfo.expiry_month}
                      onValueChange={(v) =>
                        setCardInfo((c) => ({ ...c, expiry_month: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="MM" />
                      </SelectTrigger>
                      <SelectContent>
                        {expiryMonthOptions().map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('payment.year', 'Yıl')}</Label>
                    <Select
                      value={cardInfo.expiry_year}
                      onValueChange={(v) =>
                        setCardInfo((c) => ({ ...c, expiry_year: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="YY" />
                      </SelectTrigger>
                      <SelectContent>
                        {expiryYearOptions().map((y) => (
                          <SelectItem key={y} value={y}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>CVV</Label>
                    <Input
                      inputMode="numeric"
                      maxLength={4}
                      value={cardInfo.cvv}
                      onChange={(e) =>
                        setCardInfo((c) => ({
                          ...c,
                          cvv: e.target.value.replace(/\D/g, '').slice(0, 4),
                        }))
                      }
                    />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer pt-2">
                  <Checkbox
                    checked={acceptTerms}
                    onCheckedChange={(v) => setAcceptTerms(Boolean(v))}
                  />
                  <span>
                    {t(
                      'carRental.termsAccept',
                      'Kiralama koşullarını ve iptal politikasını okudum, kabul ediyorum.'
                    )}
                  </span>
                </label>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>{t('carRental.summary', 'Özet')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {vehicle.dailyPrice != null && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('carRental.daily', 'Günlük')}</span>
                    <span>{formatPrice(Number(vehicle.dailyPrice), currency)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{t('carRental.rental', 'Kiralama')}</span>
                  <span>{formatPrice(vehicleTotal, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('carRental.extras', 'Ekstralar')}</span>
                  <span>{formatPrice(extrasTotal, currency)}</span>
                </div>
                {Number(vehicle.depositPrice) > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('carRental.deposit', 'Depozito')}</span>
                    <span>{formatPrice(Number(vehicle.depositPrice), currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t pt-3">
                  <span>{t('carRental.totalPrice', 'Toplam')}</span>
                  <span className="text-sky-800">
                    {formatPrice(grandTotal, currency)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {t(
                    'carRental.payThenBroker',
                    'Ödeme sitemiz üzerinden alınır; rezervasyon bilgisi KolayCAR broker’a iletilir.'
                  )}
                </p>
                <Button
                  className="w-full h-11 bg-sky-700 hover:bg-sky-800"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {t('carRental.confirmBooking')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
